import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { SERVICES } from "@/data/services";

const NavLabel = ({ link }) => (
  <span className="flex flex-col items-start leading-tight">
    <span aria-hidden={!link.prefix} className={`text-[9px] tracking-[0.3em] text-[#8A8F8B] ${link.prefix ? "" : "invisible"}`}>
      {link.prefix || "Our"}
    </span>
    <span>{link.label}</span>
  </span>
);

const fullLabel = (l) => (l.prefix ? `${l.prefix} ${l.label}` : l.label);

const LINKS = [
  { prefix: "Our", label: "Independence", id: "charter", testid: "nav-link-charter" },
  { prefix: "Our", label: "Fees", id: "fees", testid: "nav-link-fees" },
  { prefix: "Our", label: "Services", id: "services", testid: "nav-link-services" },
  { label: "About Us", id: "about", testid: "nav-link-about" },
  { prefix: "Our", label: "Locations", id: "locations", testid: "nav-link-locations" },
  { prefix: "Online", label: "Advice", to: "/online-advice", id: "online-advice", testid: "nav-link-online-advice" },
  { prefix: "Our", label: "Clients", id: "testimonials", testid: "nav-link-testimonials" },
];

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) {
    window.location.href = id === "top" ? "/" : `/#${id}`;
    return;
  }
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: window.innerWidth < 768 ? -96 : -128 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Header = ({ topOffset = "top-0" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 ${topOffset} z-40 transition-[background-color,border-color] duration-300 ${
        scrolled || open
          ? "bg-[#F9F8F4]/95 backdrop-blur-sm border-b border-black/10"
          : "bg-[#F9F8F4] border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 h-16 md:h-[88px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("top")}
          className="flex items-center shrink-0 mr-6 xl:mr-10"
        >
          <img
            src="/assets/nua-logo-transparent.png"
            alt="NUA Private Wealth — Independent Financial Advice"
            className="h-10 md:h-12 xl:h-16 w-auto"
            data-testid="nav-logo-image"
          />
        </button>

        <nav className="hidden lg:flex flex-1 items-center gap-5 xl:gap-6" aria-label="Primary">
          {LINKS.map((l) =>
            l.id === "services" ? (
              <div key="services" className="relative group flex" data-testid="nav-services-dropdown">
                <button
                  data-testid={l.testid}
                  onClick={() => go(l.id)}
                  aria-haspopup="true"
                  className="inline-flex items-end gap-1 whitespace-nowrap text-[11px] xl:text-xs uppercase tracking-[0.1em] xl:tracking-[0.18em] text-[#4A524D] hover:text-[#111413] transition-colors duration-300"
                >
                  <NavLabel link={l} />
                  <ChevronDown size={13} className="transition-transform duration-300 group-hover:rotate-180" />
                </button>
                <div
                  data-testid="nav-services-menu"
                  className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 focus-within:visible focus-within:opacity-100 focus-within:translate-y-0 absolute left-1/2 -translate-x-1/2 top-full pt-4 w-72 transition-[opacity,transform] duration-200"
                >
                  <div className="bg-[#F9F8F4] border border-black/10 shadow-xl py-2">
                    {SERVICES.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        data-testid={`nav-service-${s.slug}`}
                        className="block px-5 py-2.5 text-xs uppercase tracking-[0.14em] text-[#4A524D] hover:text-[#111413] hover:bg-[#EFEBE2] transition-colors duration-200"
                      >
                        {s.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : l.to ? (
              <Link
                key={l.id}
                to={l.to}
                data-testid={l.testid}
                className="whitespace-nowrap text-[11px] xl:text-xs uppercase tracking-[0.1em] xl:tracking-[0.18em] text-[#4A524D] hover:text-[#111413] transition-colors duration-300"
              >
                <NavLabel link={l} />
              </Link>
            ) : (
              <button
                key={l.id}
                data-testid={l.testid}
                onClick={() => go(l.id)}
                className="whitespace-nowrap text-[11px] xl:text-xs uppercase tracking-[0.1em] xl:tracking-[0.18em] text-[#4A524D] hover:text-[#111413] transition-colors duration-300"
              >
                <NavLabel link={l} />
              </button>
            )
          )}
          <button
            data-testid="nav-cta-book"
            onClick={() => go("contact")}
            className="ml-auto inline-flex w-[136px] xl:w-[150px] shrink-0 items-center justify-center gap-1.5 rounded-full bg-[#0A2333] px-3 py-2 text-[10px] uppercase tracking-[0.1em] leading-snug text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:bg-[#071824] hover:-translate-y-0.5"
          >
            Book a free consultation
            <ArrowUpRight size={12} />
          </button>
          <Link
            to="/advice-fee-health-check"
            data-testid="nav-link-fee-check"
            className="inline-flex w-[136px] xl:w-[150px] shrink-0 items-center justify-center gap-1.5 rounded-full border border-[#0A2333] px-3 py-2 text-[10px] uppercase tracking-[0.1em] leading-snug text-[#0A2333] transition-[background-color,color,transform] duration-300 hover:bg-[#0A2333] hover:text-[#F9F8F4] hover:-translate-y-0.5"
          >
            Advice fee health check
            <ArrowUpRight size={12} />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden -mr-2 inline-flex items-center gap-2 px-2 py-3 text-[#111413]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          <span className="text-xs uppercase tracking-[0.2em]" data-testid="nav-mobile-toggle-label">
            {open ? "Close" : "Menu"}
          </span>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black/10 bg-[#F9F8F4] px-6 py-4 flex flex-col gap-1 max-h-[calc(100dvh-160px)] overflow-y-auto" data-testid="nav-mobile-menu">
          {LINKS.map((l) =>
            l.id === "testimonials" ? (
              <div key="testimonials" className="flex flex-col gap-1">
                <button
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => go(l.id)}
                  className="text-left py-3 text-sm uppercase tracking-[0.18em] text-[#111413]"
                >
                  {fullLabel(l)}
                </button>
                <Link
                  to="/client-documents"
                  data-testid="nav-client-upload-mobile"
                  onClick={() => setOpen(false)}
                  className="text-left py-3 text-sm uppercase tracking-[0.18em] text-[#111413]"
                >
                  Client document upload
                </Link>
              </div>
            ) : l.to ? (
              <Link
                key={l.id}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                onClick={() => setOpen(false)}
                className="text-left py-3 text-sm uppercase tracking-[0.18em] text-[#111413]"
              >
                {fullLabel(l)}
              </Link>
            ) : (
              <button
                key={l.id}
                data-testid={`${l.testid}-mobile`}
                onClick={() => go(l.id)}
                className="text-left py-3 text-sm uppercase tracking-[0.18em] text-[#111413]"
              >
                {fullLabel(l)}
              </button>
            )
          )}
          <button
            data-testid="nav-cta-book-mobile"
            onClick={() => go("contact")}
            className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#0A2333] px-6 py-3.5 text-xs uppercase tracking-[0.15em] text-[#F9F8F4]"
          >
            Book a free consultation
            <ArrowUpRight size={14} />
          </button>
          <Link
            to="/advice-fee-health-check"
            data-testid="nav-link-fee-check-mobile"
            onClick={() => setOpen(false)}
            className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full border border-[#0A2333] px-6 py-3.5 text-xs uppercase tracking-[0.15em] text-[#0A2333] transition-[background-color,color] duration-300 hover:bg-[#0A2333] hover:text-[#F9F8F4]"
          >
            Advice fee health check
          </Link>
        </div>
      )}
    </header>
  );
};
